import React, { useEffect, useState } from 'react'
import { css } from '@emotion/core'
import { Paragraph, Image, Separator, WhiteTheme } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useUserProfile } from '@fs/zion-user'
import { useChineseSurnameInfo } from '../../providers/ChineseSurnameProvider'
import { useMemoryBook } from '../../providers/MemoryBookProvider'
import { getHasDarkBackground } from '../../helpers/getHasDarkBackground'
import ChineseSurnamePopulationAndOrigins from './ChineseSurnamePopulationAndOrigins'
import ChineseSurnameHeader from './ChineseSurnameHeader'
import ChineseSurnamePopulationAndOriginsPrint from './ChineseSurnamePopulationAndOriginsPrint'
import PlaceholderSurnamePage from '../PlaceholderSurnamePage/PlaceholderSurnamePage'

const FULL_WIDTH_MEANING_LENGTH = 90

const pageLayoutCss = css`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto 1fr auto auto;
  height: 100%;
  width: 100%;
  gap: 8px;
`
const realtiveMapCSS = css`
  position: relative;
`

const imageStyle = (isFullWidthMeaning) => css`
  display: flex;
  justify-content: center;
  align-items: ${isFullWidthMeaning ? 'start' : 'flex-end'};
  height: 100%;
  width: 100%;
`

const printMapCSS = css`
  display: flex;
  justify-content: center;
  height: 100%;
  width: 100%;
  align-items: start;
  text-align: center;
`

const printImageStyle = (isFullWidthMeaning) => css`
  height: auto;
  width: ${isFullWidthMeaning ? '65%' : '90%'};
`
function ChineseSurnamePage({ surnameId, color = 'purple', isPrintView = false, pageNumber }) {
  const [surnameData, setSurnameData] = useState(null)
  const [isLoading, setIsLoading] = useState(!!surnameId)
  const { fetchSurnameMaps, fetchSurnameData } = useChineseSurnameInfo()
  const { userProfile, userProfileLoading, error: userProfileError } = useUserProfile()

  // Get hasDarkBackground for this page
  const { memoryBook } = useMemoryBook()
  const hasDarkBackground = getHasDarkBackground(pageNumber, memoryBook)

  const {
    surname,
    romanization,
    meaning,
    population,
    rank: surnameRank,
    id,
    hasData,
  } = surnameData || {
    surname: '',
    romanization: '',
    meaning: '',
    population: 0,
    rank: 0,
    id: null,
    hasData: false,
  }

  const isFullWidthMeaning = meaning?.length > FULL_WIDTH_MEANING_LENGTH

  const [t] = useTranslation()

  const [originMap, setOriginMap] = useState('')
  const [distributionMap, setDistributionMap] = useState('')

  useEffect(() => {
    const currentUserSurname = userProfile?.fullName?.surname?.value

    if (!surnameId && !currentUserSurname) {
      setSurnameData(null)
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    let isMounted = true

    async function fetchData() {
      try {
        const result = await fetchSurnameData(surnameId, currentUserSurname)
        if (isMounted) {
          setSurnameData(result)
          setIsLoading(false)
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching surname data:', error)
          setIsLoading(false)
        }
      }
    }

    if (userProfile && !userProfileLoading && !userProfileError) {
      fetchData()
    }

    // eslint-disable-next-line consistent-return
    return () => {
      isMounted = false
    }
  }, [userProfile, userProfileLoading, userProfileError, surnameId, fetchSurnameData])

  useEffect(() => {
    if (!id) return

    let isMounted = true

    async function fetchImageUrls() {
      try {
        const { originMap: fetchedOriginMap, distributionMap: fetchedDistributionMap } = await fetchSurnameMaps(
          id,
          color
        )
        if (isMounted) {
          setOriginMap(fetchedOriginMap)
          setDistributionMap(fetchedDistributionMap)
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching images:', error)
        }
      }
    }

    fetchImageUrls()

    // eslint-disable-next-line consistent-return
    return () => {
      isMounted = false
    }
  }, [id, color, fetchSurnameMaps])

  const mapComponent = !isPrintView ? (
    <div css={imageStyle(isFullWidthMeaning)}>
      <Image
        src={distributionMap}
        alt={t('SurnamePage.distributionMap.caption', 'Modern Population Distribution')}
        noCrop
        height="auto"
        width={isFullWidthMeaning ? '68%' : '100%'}
      />
    </div>
  ) : (
    <div css={printMapCSS}>
      <img
        src={distributionMap}
        alt={t('SurnamePage.distributionMap.caption', 'Modern Population Distribution')}
        css={printImageStyle(isFullWidthMeaning)}
      />
    </div>
  )

  if (isLoading) {
    return null
  }

  if (!surname && !isLoading && !hasData) {
    return <PlaceholderSurnamePage pageNumber={pageNumber} isGlobalSurnamePage={false} />
  }

  const content = (
    <div css={pageLayoutCss}>
      <div css={!isFullWidthMeaning && realtiveMapCSS}>
        <ChineseSurnameHeader
          surname={surname}
          romanization={romanization}
          meaning={meaning}
          isFullWidth={isFullWidthMeaning}
          color={color}
          hasDarkBackground={hasDarkBackground}
        />
        {distributionMap && mapComponent}
      </div>
      {hasDarkBackground ? (
        <>
          <WhiteTheme>
            <Paragraph centered size="sm">
              <strong>{t('SurnamePage.distributionMap.caption', 'Modern Population Distribution')}</strong>
            </Paragraph>
          </WhiteTheme>
          {!isPrintView && <Separator size="md" />}
          {isPrintView ? (
            <ChineseSurnamePopulationAndOriginsPrint
              population={population}
              surnameRank={surnameRank}
              originMap={originMap}
              color={color}
            />
          ) : (
            <ChineseSurnamePopulationAndOrigins
              population={population}
              surnameRank={surnameRank}
              originMap={originMap}
              color={color}
            />
          )}
        </>
      ) : (
        <>
          <Paragraph centered size="sm">
            <strong>{t('SurnamePage.distributionMap.caption', 'Modern Population Distribution')}</strong>
          </Paragraph>
          {!isPrintView && <Separator size="md" />}
          {isPrintView ? (
            <ChineseSurnamePopulationAndOriginsPrint
              population={population}
              surnameRank={surnameRank}
              originMap={originMap}
              color={color}
            />
          ) : (
            <ChineseSurnamePopulationAndOrigins
              population={population}
              surnameRank={surnameRank}
              originMap={originMap}
              color={color}
            />
          )}
        </>
      )}
    </div>
  )

  return <div>{content}</div>
}

export default ChineseSurnamePage
